/**
 * Milk module constants.
 *
 * Values that vary by environment are resolved through the shared env() helper
 * from app.config.ts, which checks window.__ENV__ (runtime injection) before
 * import.meta.env (build-time) and finally falls back to a hardcoded default.
 * This ensures the constants work correctly in production, development, and the
 * Jest/CJS test environment (where @/core/config/app.config is mocked).
 */
import { env } from '@/core/config/app.config';
import i18next from 'i18next';
import type { MilkSourceType, MilkUseType } from '@/modules/milk/types/milk-management-api.types';

/** Demo receivable ID used for the Scan flow in development/demo environments. */
export const DEMO_RECEIVABLE_ID: string =
  env('VITE_DEMO_RECEIVABLE_ID', 'd3000000-0000-4000-a000-000000000001');

/** Fallback bottle ID sent to the API when the scanned receivable has no bottle_id. */
export const DEMO_BOTTLE_ID: string =
  env('VITE_DEMO_BOTTLE_ID', '00000000-0000-0000-0000-000000000000');

/** Demo provider ID used as the provider_id in POST /api/v1/milk-receivables. */
export const DEMO_PROVIDER_ID: string =
  env('VITE_DEMO_PROVIDER_ID', 'd1000000-0000-4000-a000-000000000001');

/** Hospital ID sent as hospital_id in POST /api/v1/pools. */
export const DEMO_HOSPITAL_ID: string =
  env('VITE_DEMO_HOSPITAL_ID', '00000000-0000-4000-a000-000000000007');

/** User ID sent as created_by in POST /api/v1/pools. */
export const DEMO_CREATED_BY: string =
  env('VITE_DEMO_CREATED_BY', '00000000-0000-4000-a000-00000000000d');

const ALLOWED_DEMO_SOURCE_TYPES = ['MOM', 'DONOR'] as const;
const ALLOWED_DEMO_USE_TYPES = ['RAW', 'PROCESSED'] as const;
function parseDemoSourceType(value: string, fallback: MilkSourceType): MilkSourceType {
  return ALLOWED_DEMO_SOURCE_TYPES.includes(value as (typeof ALLOWED_DEMO_SOURCE_TYPES)[number])
    ? (value as MilkSourceType)
    : fallback;
}
function parseDemoUseType(value: string, fallback: MilkUseType): MilkUseType {
  return ALLOWED_DEMO_USE_TYPES.includes(value as (typeof ALLOWED_DEMO_USE_TYPES)[number])
    ? (value as MilkUseType)
    : fallback;
}
/** source_type sent to POST /api/v1/milk-receivables. Must be "MOM" or "DONOR". */
export const DEMO_SOURCE_TYPE: MilkSourceType = parseDemoSourceType(
  env('VITE_DEMO_SOURCE_TYPE', 'DONOR'),
  'DONOR'
);
/** use_type sent to POST /api/v1/milk-receivables. Must be "RAW" or "PROCESSED". */
export const DEMO_USE_TYPE: MilkUseType = parseDemoUseType(
  env('VITE_DEMO_USE_TYPE', 'RAW'),
  'RAW'
);

/**
 * Maps a raw API pool_status value to a human-readable display string.
 * Handles both singular ('QUARANTINE') and past-tense ('QUARANTINED') forms
 * that the backend may emit.
 *
 * Note: pool_status can no longer be FAILED — pasteurization temperature and
 * hold time are selected from a controlled dropdown of pre-approved
 * combinations, so a pool always advances to Quarantined on save.
 */
export function formatPoolStatus(rawStatus: string): string {
  switch (rawStatus.toUpperCase()) {
    case 'OPEN':
    case 'PREP':
      return i18next.t('milk.statusPrep', 'Prep');
    case 'QUARANTINE':
    case 'QUARANTINED':
      return i18next.t('milk.statusQuarantined', 'Quarantined');
    case 'CLOSED':
      return i18next.t('feed.statusCompleted', 'Completed');
    default:
      return rawStatus;
  }
}
